import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  getConfig,
  guiasPendientesDeRecepcion,
  recibirFojaCompletaV2,
  recibirGuiaEnDestino,
  recepcionQrScan,
  validarRecepcionQr,
} from '@/services/recepcionCarga.js';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import {
  CargasPage,
  CargasSection,
  CargasStickyActions,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
  cargasPrimaryButtonClassName,
  useSaveShortcut,
} from './components/CargasLayout.jsx';

const MODOS = {
  GUIA_POR_GUIA: 'GUIA_POR_GUIA',
  MARCAR_TODO: 'MARCAR_TODO',
  QR_OBLIGATORIO: 'QR_OBLIGATORIO',
};

function getSucursalId(sucursal, user) {
  return Number(sucursal?.id || sucursal?.sucursal_id || user?.sucursal_id || user?.sucursalId || 0);
}

export default function RecepcionOperativa() {
  const { sucursalActiva, sucursal, user } = useAuth();
  const sucursalId = getSucursalId(sucursalActiva || sucursal, user);
  const usuarioId = Number(user?.id || user?.user_id || 0);

  const [modo, setModo] = useState(MODOS.GUIA_POR_GUIA);
  const [guias, setGuias] = useState([]);
  const [fojaId, setFojaId] = useState('');
  const [qr, setQr] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [geo, setGeo] = useState({ lat: null, lng: null });

  const guiasPorFoja = useMemo(() => {
    const map = new Map();
    guias.forEach(g => {
      const key = g.fojaId || 'SIN_FOJA';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    });
    return map;
  }, [guias]);

  const clearFeedback = () => {
    setMessage('');
    setError('');
  };

  const loadData = async () => {
    if (!sucursalId) return;
    setLoading(true);
    clearFeedback();
    try {
      const [cfg, pending] = await Promise.all([
        getConfig(String(sucursalId), 'recepcion_modo'),
        guiasPendientesDeRecepcion({ sucursalId: String(sucursalId) }),
      ]);
      setModo(cfg?.value || MODOS.GUIA_POR_GUIA);
      setGuias(Array.isArray(pending) ? pending : []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar recepcion.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [sucursalId]);

  useEffect(() => {
    if (!navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      position => setGeo({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => setGeo({ lat: null, lng: null }),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 30000 },
    );
  }, []);

  const onRecibirGuia = async guia => {
    clearFeedback();
    try {
      await recibirGuiaEnDestino({
        guiaId: guia.id,
        sucursalDestinoId: String(sucursalId),
        usuarioId: String(usuarioId),
        metadataJson: { source: 'WEB_MANUAL' },
      });
      setMessage(`Guia ${guia.numero || guia.id} recibida OK.`);
      await loadData();
    } catch (e) {
      setError(e.message || 'Error al recibir guia.');
    }
  };

  const onRecibirTodo = async () => {
    clearFeedback();
    if (!fojaId) {
      setError('Debe indicar una foja.');
      return;
    }
    try {
      const result = await recibirFojaCompletaV2({
        fojaId: String(fojaId),
        sucursalDestinoId: String(sucursalId),
        usuarioId: String(usuarioId),
      });
      setMessage(`Recepcion masiva finalizada. OK: ${result.ok}, Error: ${result.error}.`);
      await loadData();
    } catch (e) {
      setError(e.message || 'Error en recepcion de foja.');
    }
  };

  const onQrScan = async () => {
    clearFeedback();
    if (!qr.trim()) {
      setError('Ingrese o pegue un codigo QR.');
      return;
    }
    try {
      const codigoQr = qr.trim();
      const validation = await validarRecepcionQr(codigoQr);
      if (!validation?.valid) {
        throw new Error(validation?.message || 'Codigo QR invalido.');
      }

      const guia = await recepcionQrScan({
        codigoQr,
        sucursalDestinoId: String(sucursalId),
        usuarioId: String(usuarioId),
        device: navigator?.userAgent || 'WEB',
        lat: geo.lat,
        lng: geo.lng,
      });
      setMessage(`Guia ${guia.numero || guia.id} recibida OK por QR.`);
      setQr('');
      await loadData();
    } catch (e) {
      setError(e.message || 'QR invalido o transicion bloqueada.');
    }
  };

  const handlePrimary = () => {
    if (modo === MODOS.MARCAR_TODO) {
      void onRecibirTodo();
      return;
    }
    if (modo === MODOS.QR_OBLIGATORIO) {
      void onQrScan();
    }
  };

  useSaveShortcut(handlePrimary, modo !== MODOS.GUIA_POR_GUIA);

  return (
    <CargasPage
      title="Recepcion en destino"
      description={`Sucursal: ${sucursalId || 'N/D'} - Modo: ${modo}`}
      hideHeaderOnCompact
      footer={
        modo !== MODOS.GUIA_POR_GUIA ? (
          <CargasStickyActions
            summary={<div className="text-xs text-[rgba(var(--muted),0.92)]">Guias pendientes: {guias.length}</div>}
            actions={
              <button type="button" className={cargasPrimaryButtonClassName} onClick={handlePrimary}>
                {modo === MODOS.MARCAR_TODO ? 'Recibir todo' : 'Confirmar QR'}
              </button>
            }
          />
        ) : null
      }
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        {message ? (
          <div className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] px-4 py-3 text-sm">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] px-4 py-3 text-sm text-[rgb(var(--brand))]">
            {error}
          </div>
        ) : null}

        <CargasSection
          title="Operacion"
          className="col-span-12 md:col-span-8"
          bodyClassName="min-h-0 space-y-3 p-4"
        >
          {loading ? <p className="text-sm text-[rgba(var(--muted),0.92)]">Cargando...</p> : null}

          {!loading && modo === MODOS.GUIA_POR_GUIA ? (
            guias.length ? (
              <CargasTableShell className="max-h-[300px]">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 bg-[rgb(var(--panel))]">
                    <tr>
                      <th className="px-3 py-2">Guia</th>
                      <th className="px-3 py-2 text-right">Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guias.map(guia => (
                      <tr key={guia.id} className="border-t border-[rgba(var(--border),0.45)]">
                        <td className="px-3 py-2">#{guia.numero || guia.id}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" className={cargasPrimaryButtonClassName} onClick={() => onRecibirGuia(guia)}>
                            Recibir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CargasTableShell>
            ) : (
              <EmptyState title="Sin guias pendientes" description="No hay guias pendientes en esta sucursal." />
            )
          ) : null}

          {!loading && modo === MODOS.MARCAR_TODO ? (
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12 md:col-span-6">
                <Field label="ID de foja" htmlFor="fojaId">
                  <input id="fojaId" value={fojaId} onChange={e => setFojaId(e.target.value)} className={cargasInputClassName} />
                </Field>
              </div>
            </div>
          ) : null}

          {!loading && modo === MODOS.QR_OBLIGATORIO ? (
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12">
                <Field label="Codigo QR" htmlFor="qr">
                  <input
                    id="qr"
                    value={qr}
                    onChange={e => setQr(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void onQrScan();
                      }
                    }}
                    className={cargasInputClassName}
                  />
                </Field>
              </div>
            </div>
          ) : null}
        </CargasSection>

        <CargasSection title="Contexto" className="col-span-12 md:col-span-4" bodyClassName="space-y-2 p-4 text-sm">
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Modo: {modo}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Pendientes: {guias.length}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Fojas detectadas: {[...guiasPorFoja.keys()].join(', ') || 'sin datos'}
          </div>
          {modo !== MODOS.GUIA_POR_GUIA ? (
            <button type="button" className={cargasButtonClassName} onClick={handlePrimary}>
              {modo === MODOS.MARCAR_TODO ? 'Recibir todo' : 'Confirmar QR'}
            </button>
          ) : null}
        </CargasSection>
      </CargasViewport>
    </CargasPage>
  );
}
